import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { resolveMedia } from "@/lib/media";
import { useLenis } from "lenis/react";

export default function Hero() {
  const { content } = useSite();
  const hero = content?.hero || {};
  const lenis = useLenis();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const fadeText = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  // 3D tilt
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const rotX = useSpring(useTransform(mvY, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const rotY = useSpring(useTransform(mvX, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 14 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mvX.set((e.clientX - r.left) / r.width - 0.5);
    mvY.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mvX.set(0);
    mvY.set(0);
  };

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) (lenis ? lenis.scrollTo(el, { offset: -72 }) : el.scrollIntoView({ behavior: "smooth" }));
  };

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-24"
    >
      {/* background glow */}
      <div className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[color:var(--aurex-accent)] opacity-[0.10] blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[420px] w-[420px] rounded-full bg-[color:var(--aurex-accent-2)] opacity-[0.06] blur-[130px]" />

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-6 md:px-10 lg:grid-cols-12 lg:gap-6">
        <motion.div style={{ opacity: fadeText }} className="lg:col-span-7">
          <MaskLine delay={0.1}>
            <span className="font-mono-jb text-xs uppercase tracking-[0.35em] text-[color:var(--aurex-accent-soft)]">
              {hero.overline || "Notebooks de alta gama"}
            </span>
          </MaskLine>

          <h1 className="mt-6 font-display text-[15vw] font-extrabold leading-[0.86] tracking-tighter sm:text-[13vw] lg:text-[8.2vw] 2xl:text-[150px]">
            <MaskLine delay={0.2} className="silver-text">
              {hero.title_line_1 || "Potencia"}
            </MaskLine>
            <MaskLine delay={0.34} className="copper-text">
              {hero.title_line_2 || "para crear."}
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 max-w-md text-base leading-relaxed text-[color:var(--aurex-muted)] md:text-lg"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#productos")}
              className="group flex items-center gap-2 rounded-full copper-bg px-7 py-3.5 text-sm font-semibold text-[color:var(--aurex-on-accent)] transition-transform duration-300 hover:scale-[1.04]"
            >
              {hero.cta_primary || "Explorar equipos"}
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#video")}
              className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-[color:var(--aurex-text)] transition-colors duration-300 hover:border-white/40 hover:text-white"
            >
              {hero.cta_secondary || "Ver video"}
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
            className="spotlight relative"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[1.6rem] border border-white/10"
            >
              <motion.img
                src={resolveMedia(hero.image)}
                alt="AUREX notebook"
                style={{ y: yImg, scale: scaleImg }}
                className="h-[380px] w-full object-cover md:h-[560px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              {hero.side_card_visible !== false && (hero.side_card_overline || hero.side_card_title) && (
                <div
                  style={{ transform: "translateZ(60px)" }}
                  className="absolute bottom-5 left-5 rounded-2xl glass border border-white/10 px-5 py-3"
                >
                  <div className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[color:var(--aurex-muted)]">{hero.side_card_overline || "Performance"}</div>
                  <div className="font-display text-lg font-bold text-[color:var(--aurex-text)]">{hero.side_card_title || "RTX™ · DDR5 · NVMe"}</div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("#manifiesto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-[color:var(--aurex-muted)] md:flex"
      >
        <span className="font-mono-jb text-[10px] uppercase tracking-[0.3em]">{hero.scroll_label || "Scroll"}</span>
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
